import { LogIn, LogOut } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { supabaseConfigured } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Dropdown } from '@/components/ui/Dropdown';
import { Tooltip } from '@/components/ui/Tooltip';

/**
 * Sign-in/sign-out control for the public header. Renders one of three
 * states: nothing while the session restore is in flight (avoids a
 * sign-in-button flash for an already-authenticated visitor), a Google
 * sign-in button for guests, or the account menu for a signed-in user.
 */
export function UserMenu() {
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const signInWithGoogle = useAuthStore((state) => state.signInWithGoogle);
  const signOut = useAuthStore((state) => state.signOut);

  if (status === 'loading') return null;

  if (status === 'guest') {
    const button = (
      <Button
        variant="outline"
        size="sm"
        leadingIcon={<LogIn className="h-3.5 w-3.5" />}
        disabled={!supabaseConfigured}
        onClick={() => void signInWithGoogle()}
      >
        Sign in with Google
      </Button>
    );

    if (supabaseConfigured) return button;

    return (
      <Tooltip content="Google sign-in is not configured for this deployment yet.">
        {button}
      </Tooltip>
    );
  }

  const label = user?.name || user?.email || 'Account';
  const initial = label.charAt(0).toUpperCase();

  return (
    <Dropdown
      label={`Account: ${label}`}
      trigger={({ toggle, open, id }) => (
        <button
          id={id}
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Account menu for ${label}`}
          className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-accent text-xs font-semibold text-accent-fg"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            initial
          )}
        </button>
      )}
      items={[
        {
          id: 'sign-out',
          label: 'Sign out',
          icon: <LogOut className="h-3.5 w-3.5" aria-hidden="true" />,
          onSelect: () => void signOut(),
        },
      ]}
    />
  );
}
